import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Eagerly load the homepage
import Index from "./pages/Index";

// Lazy-load all other pages
const Login = lazy(() => import("./pages/Login"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Help = lazy(() => import("./pages/Help"));
const Contact = lazy(() => import("./pages/Contact"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Apply = lazy(() => import("./pages/Apply"));
const Register = lazy(() => import("./pages/Register"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const DataPolicy = lazy(() => import("./pages/DataPolicy"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Drawdown = lazy(() => import("./pages/Drawdown"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDeals = lazy(() => import("./pages/AdminDeals"));
const AdminDealPage = lazy(() => import("./pages/AdminDealPage"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminUserPage = lazy(() => import("./pages/AdminUserPage"));
const AdminFinancials = lazy(() => import("./pages/AdminFinancials"));
const AdminAvailability = lazy(() => import("./pages/AdminAvailability"));
const AdminTeam = lazy(() => import("./pages/AdminTeam"));
const Account = lazy(() => import("./pages/Account"));
const DealSubmitted = lazy(() => import("./pages/DealSubmitted"));
const Acknowledge = lazy(() => import("./pages/Acknowledge"));
const ExchangeUpload = lazy(() => import("./pages/ExchangeUpload"));
const Report = lazy(() => import("./pages/Report"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminInsights = lazy(() => import("./pages/AdminInsights"));
const AdminInsightEdit = lazy(() => import("./pages/AdminInsightEdit"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightArticle = lazy(() => import("./pages/InsightArticle"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const AdminDiscounts = lazy(() => import("./pages/AdminDiscounts"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/what-we-pay" element={<Pricing />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/submit-deal" element={<Apply />} />
            <Route path="/deal-submitted" element={<DealSubmitted />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-policy" element={<DataPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />
            <Route path="/drawdown/:dealId" element={<Drawdown />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/deals" element={<AdminDeals />} />
            <Route path="/admin/deals/:dealId" element={<AdminDealPage />} />
            <Route path="/admin/financials" element={<AdminFinancials />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:userId" element={<AdminUserPage />} />
            <Route path="/admin/calendar" element={<AdminAvailability />} />
            <Route path="/admin/team" element={<AdminTeam />} />
            <Route path="/acknowledge/:dealId" element={<Acknowledge />} />
            <Route path="/exchange/:dealId" element={<ExchangeUpload />} />
            <Route path="/report" element={<Report />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/insights" element={<AdminInsights />} />
            <Route path="/admin/insights/:id" element={<AdminInsightEdit />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/admin/referrals" element={<AdminReferrals />} />
            <Route path="/admin/discounts" element={<AdminDiscounts />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
